/**
 * Backend transport abstraction. `TauriClientSdk` (default under `tauri dev`/a bundled app)
 * forwards every call to the Rust side over Tauri's IPC bridge, exactly as before this
 * abstraction existed. `BrowserClientSdk` (used when the app is opened as a plain web page,
 * with no Tauri runtime present) talks to the remote REST API directly instead.
 *
 * Command names and argument shapes are shared between both implementations — they mirror the
 * existing `#[tauri::command]` surface (`src-tauri/src/commands/*.rs`) so call sites written
 * against `invoke(cmd, args)` don't need to know or care which implementation is live. Not every
 * command has a meaningful browser-mode equivalent (native screen/system-audio capture, the local
 * on-disk media/attachment cache, the desktop updater, bots/interactions/agent-credentials — none
 * of which have a documented REST endpoint yet); `BrowserClientSdk` rejects those with
 * `BrowserUnsupportedCommandError` instead of pretending to support them.
 */
export interface ClientSdk {
  readonly kind: "tauri" | "browser"
  invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T>
}

/** Thrown by `BrowserClientSdk` for commands that only make sense inside the native Tauri app. */
export class BrowserUnsupportedCommandError extends Error {
  constructor(cmd: string) {
    super(`"${cmd}" is not available in the browser build — this feature requires the desktop app.`)
    this.name = "BrowserUnsupportedCommandError"
  }
}
